import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/Auth';
import { useCatalogStatus } from '../lib/Catalog';
import { useLibrary } from '../lib/Library';
import { debridLabel, debridName } from '../lib/format';
import Logo from './Logo';

// How many titles the quick dropdown offers before handing over to the full page
const QUICK_RESULTS = 6;

/**
 * The nav. Series and Films are Discover filtered by type, so they light up on
 * the type in the query string rather than on the path alone.
 */
const NAV = [
  { to: '/home', label: 'Home', active: (l) => l.pathname === '/home' },
  {
    to: '/discover?type=series',
    label: 'Series',
    active: (l) => l.pathname === '/discover' && new URLSearchParams(l.search).get('type') === 'series'
  },
  {
    to: '/discover?type=movie',
    label: 'Films',
    active: (l) => l.pathname === '/discover' && new URLSearchParams(l.search).get('type') === 'movie'
  },
  { to: '/sports', label: 'Sports', active: (l) => l.pathname === '/sports' },
  { to: '/tv', label: 'Live TV', active: (l) => l.pathname === '/tv' },
  { to: '/list', label: 'My List', active: (l) => l.pathname === '/list' }
];

/**
 * The fixed header: wordmark, nav, a search that unfolds from its icon, the
 * debrid status, and the account menu.
 *
 * It floats transparent over the home hero and turns solid once the page has
 * scrolled or on any screen that doesn't open on artwork. The sidebar it
 * replaced carried the catalog presets; those now live at the top of Discover.
 */
const Header = ({ indexers, debrid }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, leaveProfile } = useAuth();
  const { configured } = useCatalogStatus();
  const { items } = useLibrary();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  // Serial number of the latest keystroke's fetch, so a slow answer for an old
  // query can never overwrite a fresh one
  const seq = useRef(0);

  // A modal title sits over the page it was opened from; that page decides the
  // header's tone, not the modal route.
  const shown = location.state?.background || location;
  const onHome = shown.pathname === '/home';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Anything open closes on a click elsewhere or on Escape; "/" opens search.
  useEffect(() => {
    const onClick = () => setMenuOpen(false);
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setSearchOpen(false);
        setQuery('');
        return;
      }
      if (event.key !== '/' || event.metaKey || event.ctrlKey) return;
      const tag = event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      event.preventDefault();
      setSearchOpen(true);
    };
    document.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // Leaving a page folds the search and the menu away
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setQuery('');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  // Quick results while typing. Only against a configured catalog — without
  // one there is nothing fast to offer and Enter still reaches the search page.
  useEffect(() => {
    const trimmed = query.trim();
    if (!configured || trimmed.length < 2) {
      setResults([]);
      setActive(-1);
      return undefined;
    }

    const mine = ++seq.current;
    const timer = setTimeout(() => {
      axios
        .get(`/api/catalog/search?q=${encodeURIComponent(trimmed)}`)
        .then(({ data }) => {
          if (seq.current !== mine) return;
          setResults((data.data?.titles || []).slice(0, QUICK_RESULTS));
          setActive(-1);
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [query, configured]);

  const goTo = (title) => {
    setSearchOpen(false);
    setQuery('');
    navigate(`/title/${title.type}/${title.id}`, { state: { background: shown } });
  };

  const goToSearch = () => {
    if (!query.trim()) return;
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (active >= 0 && results[active]) goTo(results[active]);
    else goToSearch();
  };

  const onKeyDown = (event) => {
    if (!results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((value) => (value + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((value) => (value <= 0 ? results.length - 1 : value - 1));
    }
  };

  // The profile's initial, not the account's: four people sharing account "107"
  // would otherwise all sit behind the same "1".
  const letter = (user?.profile?.name || user?.username || '?').slice(0, 1).toUpperCase();
  const solid = scrolled || !onHome;

  return (
    <header className={`top${solid ? ' is-solid' : ''}`}>
      <Link to="/home" className="brand" aria-label="watchanything.stream">
        <Logo />
      </Link>

      <nav className="topnav">
        {NAV.map((item) => (
          <Link key={item.to} to={item.to} className={`topnav-item${item.active(shown) ? ' is-active' : ''}`}>
            {item.label}
            {item.to === '/list' && items.length > 0 && <span className="topnav-count">{items.length}</span>}
          </Link>
        ))}
      </nav>

      <div className="top-right">
        <form
          className={`topsearch${searchOpen ? ' is-open' : ''}`}
          onSubmit={onSubmit}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="topsearch-icon"
            aria-label="Search"
            onClick={() => setSearchOpen((open) => !open)}
          />
          {searchOpen && (
            <>
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Titles, people, live events"
                aria-label="Search the catalog"
                autoComplete="off"
              />
              <span className="key-hint">esc</span>
            </>
          )}

          {searchOpen && results.length > 0 && (
            <div className="quick" role="listbox">
              {results.map((title, index) => (
                <button
                  key={`${title.type}-${title.id}`}
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  className={`quick-row${index === active ? ' is-active' : ''}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    goTo(title);
                  }}
                  onMouseEnter={() => setActive(index)}
                >
                  <span className="quick-art">
                    {title.poster && <img src={title.poster} alt="" loading="lazy" />}
                  </span>
                  <span className="quick-name">
                    <b>{title.name}</b>
                    <span>
                      {[title.type === 'series' ? 'Series' : 'Film', title.year].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {title.rating ? <span className="quick-rating">★ {title.rating}</span> : null}
                </button>
              ))}
              <button
                type="button"
                className="quick-all"
                onMouseDown={(event) => {
                  event.preventDefault();
                  goToSearch();
                }}
              >
                See all results for “{query.trim()}” →
              </button>
            </div>
          )}
        </form>

        <span className="top-status">{debridLabel(debrid)}</span>

        <div className="usermenu" onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="avatar-btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="avatar">{letter}</span>
            <span className={`caret${menuOpen ? ' is-up' : ''}`} />
          </button>

          {menuOpen && (
            <div className="menu" role="menu">
              <span className="menu-arrow" />
              <div className="menu-head">
                <span className="avatar avatar--sm">{letter}</span>
                <span className="menu-name">{user?.profile?.name || user?.username}</span>
              </div>
              {(user?.profiles?.length || 0) > 1 && (
                <button type="button" className="menu-item" role="menuitem" onClick={leaveProfile}>
                  Switch profile
                </button>
              )}
              <Link to="/data" className="menu-item" role="menuitem">
                Settings
              </Link>
              {user?.admin && (
                <Link to="/admin" className="menu-item" role="menuitem">
                  Admin
                </Link>
              )}
              <div className="menu-rule" />
              <button type="button" className="menu-item" role="menuitem" onClick={signOut}>
                Sign out of watchanything
              </button>
              <div className="menu-foot">
                {[
                  user?.profile ? `${user.username} · ${user.profile.name}` : user?.username,
                  indexers > 0 ? `${indexers} indexers` : 'no indexers',
                  debrid?.enabled ? debridName(debrid) : 'p2p'
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
